import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Grid,
  Paper,
  useTheme,
  styled,
  Divider,
  IconButton,
  useMediaQuery,
  Chip
} from '@mui/material';
import {
  Code,
  Work,
  Email,
  LinkedIn,
  GitHub
} from '@mui/icons-material';
import React from 'react';

const FloatingCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: theme.shadows[10]
  }
}));

const About = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const stats = [
    {
      value: '8+',
      label: 'Years building cool stuff'
    },
    {
      value: '85%',
      label: 'Test coverage (always)'
    },
    {
      value: '∞',
      label: 'Coffee cups needed'
    },
    {
      value: '1',
      label: 'Passion for AI'
    }
  ];

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={6} md={4}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Paper elevation={4} sx={{
              background: '#fff',
              border: '1px solid #e3f2fd',
              boxShadow: '0 2px 12px rgba(21,101,192,0.06)',
              height: 280,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'box-shadow 0.2s, border-color 0.2s',
              '&:hover': {
                boxShadow: '0 6px 24px rgba(21,101,192,0.12)',
                borderColor: '#2196f3',
              },
              p: 3,
            }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#1565c0', fontSize: '1.5rem' }}>
                🚀 Who am I?
              </Typography>
              <Typography variant="body2" sx={{ color: '#333', textAlign: 'center', lineHeight: 1.6 }}>
                Full-stack engineer obsessed with building pixel-perfect, lightning-fast UIs and exploring generative AI. Code tinkerer, performance enthusiast, and believer that great software is an art form. Currently diving deep into LLMs, prompt engineering, and agentic workflows.
              </Typography>
            </Paper>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Paper elevation={4} sx={{
              background: '#fff',
              border: '1px solid #e3f2fd',
              boxShadow: '0 2px 12px rgba(21,101,192,0.06)',
              height: 280,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'box-shadow 0.2s, border-color 0.2s',
              '&:hover': {
                boxShadow: '0 6px 24px rgba(21,101,192,0.12)',
                borderColor: '#2196f3',
              },
              p: 3,
            }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#1565c0', mb: 2 }}>
                📊 Quick Stats
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                {stats.map((stat, index) => (
                  <motion.div key={index}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#e3f2fd',
                      minWidth: 75,
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1565c0', fontSize: '1rem', textAlign: 'center' }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#333', textAlign: 'center', fontSize: '0.65rem', lineHeight: 1.2 }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </Paper>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Paper elevation={4} sx={{
              background: '#fff',
              border: '1px solid #e3f2fd',
              boxShadow: '0 2px 12px rgba(21,101,192,0.06)',
              height: 280,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'box-shadow 0.2s, border-color 0.2s',
              '&:hover': {
                boxShadow: '0 6px 24px rgba(21,101,192,0.12)',
                borderColor: '#2196f3',
              },
              p: 3,
            }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#1565c0', fontSize: '1.5rem' }}>
                💬 Let's Connect
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', mb: 2, fontSize: '0.9rem' }}>
                Have a project in mind? Let's chat!
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 1 }}>
                <motion.div whileHover={{ scale: 1.2, rotate: 10 }}>
                  <IconButton
                    href="https://linkedin.com/in/shaileshkyadav24"
                    target="_blank"
                    sx={{ color: '#0077b5', bgcolor: '#e3f2fd', borderRadius: 2, boxShadow: '0 2px 8px rgba(21,101,192,0.10)', fontSize: '1.2rem' }}
                  >
                    <LinkedIn fontSize="large" />
                  </IconButton>
                </motion.div>
                <motion.div whileHover={{ scale: 1.2, rotate: -10 }}>
                  <IconButton
                    href="https://github.com/shaileshyadav24"
                    target="_blank"
                    sx={{ color: '#333', bgcolor: '#e3f2fd', borderRadius: 2, boxShadow: '0 2px 8px rgba(21,101,192,0.10)' }}
                  >
                    <GitHub fontSize="large" />
                  </IconButton>
                </motion.div>
              </Box>
            </Paper>
          </motion.div>
        </Grid>
      </Grid>
    </Box>

  );
};

export default About;